import { OrderTrackingOrderSource } from "@redotech/order-tracking-common/order-tracking-types";
import { TrackingPageProductUpsellBlock } from "@redotech/order-tracking-common/tracking-page-definition";
import { IterableMap } from "@redotech/react-util/component";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { DiscountSettings } from "@redotech/redo-discounts-common/discount/discount-db-parser";
import { TrackingPageDiscountSource } from "@redotech/redo-discounts-common/generated-discount-code-definition";
import { useTranslation } from "@redotech/redo-i18n";
import { Provider } from "@redotech/redo-order-common/provider";
import { isBlacklistedProductName } from "@redotech/redo-product-common/product-blacklist";
import { RedoRecommendedProduct } from "@redotech/redo-product-common/redo-recommended-product-schema";
import { isDef } from "@redotech/util/checks";
import classNames from "classnames";
import { memo, useMemo } from "react";
import { Flex } from "../../../flex";
import { ExternalLink } from "../../../link";
import { Text } from "../../../text";
import {
  TrackingPageAccessMode,
  TrackingPageContext,
  TrackingPageEnvironment,
} from "../../context/tracking-page-context";
import { getHeaderStyles, getLinkStyles } from "../shared/block-styles";
import { BlockWrapper } from "../shared/block-wrapper";
import {
  getInitialExpirationDuration,
  getUpsellText,
} from "./discount-handling";
import { ExpirationCountdown } from "./expiration-countdown";
import * as productUpsellCss from "./product-upsell.module.css";
import { RecommendedProductComponent } from "./recommended-product";
import { resolveRecommendationProvider } from "./resolve-provider";
import { createUpsellRedirectHandler } from "./upsell-redirect";

// Stable empty reference so the customer-portal path doesn't get a fresh array
// on every render (which would thrash the recommendations useLoad deps).
const EMPTY_PROVIDERS: readonly Provider[] = [];

export const ProductUpsell = memo(function ProductUpsell({
  _id,
  recommendedProductFilterId,
  upsellText,
  buttonText,
  imageAspectRatio,
  discountId,
  shopFullUrl,
  checkoutButtonSettings,
  dropdownSettings,
  provider: blockProvider,
  ctaDestination,
  showCompareAtPrice,
  hidePrice,
}: TrackingPageProductUpsellBlock) {
  const context = useRequiredContext(TrackingPageContext);
  const { t } = useTranslation();
  const {
    order,
    returnAppSettings,
    environment,
    productUpsell,
    pageSettings,
    generateDiscountCode,
  } = context;
  const isTrackingCodeOnly =
    context.environment === TrackingPageEnvironment.CUSTOMER_PORTAL &&
    context.accessMode === TrackingPageAccessMode.TRACKING_CODE_ONLY;

  const builderDefaultProvider =
    context.environment === TrackingPageEnvironment.BUILDER
      ? context.defaultProvider
      : undefined;
  const builderAvailableProviders =
    context.environment === TrackingPageEnvironment.BUILDER
      ? context.availableProviders
      : EMPTY_PROVIDERS;

  const rehydrationCounter =
    (context.environment === TrackingPageEnvironment.BUILDER
      ? context.blockRehydrationCounters?.[_id]
      : undefined) ?? 0;

  // Resolved during render, not only inside the load: the redirect handlers
  // built below must key on the provider the products actually came from.
  const resolvedProvider: OrderTrackingOrderSource | undefined = useMemo(() => {
    if (!order?.source) {
      return undefined;
    }
    return resolveRecommendationProvider({
      blockProvider,
      environment: context.environment,
      defaultProvider: builderDefaultProvider,
      availableProviders: builderAvailableProviders,
      orderSource: order.source,
    });
  }, [
    blockProvider,
    context.environment,
    builderDefaultProvider,
    builderAvailableProviders,
    order?.source,
  ]);

  const recommendedProductsLoad = useLoad(async (): Promise<
    RedoRecommendedProduct[]
  > => {
    if (!order?.source || !resolvedProvider) {
      return [];
    }

    const allProductIds = order.lineItems
      .map((lineItem) => lineItem.productId)
      .filter(isDef);

    // Prefer line items that aren't package protection, gift cards, store
    // credit, etc. — those make for lousy "related product" seeds. Keep the
    // blacklisted items only if they're the only thing in the order so we
    // still have something to seed recommendations with.
    const nonBlacklisted = order.lineItems.filter(
      (lineItem) =>
        !isBlacklistedProductName(lineItem.name, lineItem.variantTitle),
    );
    const seedLineItems =
      nonBlacklisted.length > 0 ? nonBlacklisted : order.lineItems;
    const seedProductIds = seedLineItems
      .map((lineItem) => lineItem.productId)
      .filter(isDef);

    const recommendedProducts =
      await productUpsell.getRecommendedProductsByProvider({
        provider: resolvedProvider,
        numRecommendations: 3,
        excludedVariantIds: order.lineItems
          .map((lineItem) => lineItem.variantId)
          .filter(isDef),
        excludedParentProductIds: allProductIds,
        customer: {
          email: isTrackingCodeOnly ? undefined : order.customer?.email,
        },
        recommendedProductFilterId: recommendedProductFilterId ?? undefined,
        productIds: seedProductIds,
        orderId:
          context.environment === TrackingPageEnvironment.CUSTOMER_PORTAL
            ? order.internalMongoId
            : undefined,
      });
    return recommendedProducts.products;
  }, [
    order,
    recommendedProductFilterId,
    resolvedProvider,
    rehydrationCounter,
    isTrackingCodeOnly,
  ]);

  // Fetch discount and generate code if discountId is provided
  const directDiscountLoad = useLoad<
    { discountCode?: string; discountSettings?: DiscountSettings } | undefined
  >(async () => {
    if (
      isTrackingCodeOnly ||
      !order ||
      !discountId ||
      order.source !== Provider.SHOPIFY
    ) {
      return undefined;
    }

    const { discountCode, discountSettings } = await generateDiscountCode(
      discountId,
      TrackingPageDiscountSource.UPSELL,
      order.internalMongoId,
    );

    return { discountCode, discountSettings };
  }, [order?.internalMongoId, discountId, isTrackingCodeOnly]);

  const displayUpsellText = getUpsellText(
    upsellText,
    directDiscountLoad.value?.discountSettings,
  );
  const initialMilliseconds = getInitialExpirationDuration(
    directDiscountLoad.value?.discountSettings,
  );

  const storeURL = `https://${returnAppSettings.storeUrl}`;
  const headerStyles = getHeaderStyles(pageSettings);
  const linkStyles = getLinkStyles(pageSettings);

  const activeDiscountCode = directDiscountLoad.value?.discountCode;

  const isLoading =
    recommendedProductsLoad.pending || directDiscountLoad.pending || !order;

  return (
    <BlockWrapper
      className={classNames(productUpsellCss.upsellProductsCard, {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        [productUpsellCss.editMode]:
          environment === TrackingPageEnvironment.BUILDER,
      })}
      environment={environment}
      loading={isLoading}
      pageSettings={pageSettings}
      withBorder
      withScrollbar
    >
      {order && (
        <Flex dir="column" height="full" p="3xl">
          <Flex
            dir="column"
            inert={environment === TrackingPageEnvironment.BUILDER}
          >
            <Flex
              align="center"
              className={productUpsellCss.headerRow}
              flexDirection="row"
              justify="space-between"
            >
              <Text fontSize="lg" fontWeight="semibold" style={headerStyles}>
                {displayUpsellText}
              </Text>
              <ExternalLink
                onClick={async () => {
                  if (
                    environment === TrackingPageEnvironment.CUSTOMER_PORTAL &&
                    !shopFullUrl
                  ) {
                    const upsellRedirect = createUpsellRedirectHandler({
                      order,
                      selectWindow: productUpsell.selectWindow,
                      returnAppSettings,
                      logPageClick: productUpsell.logPageClick,
                      redirectToWebsiteWithDiscountCode:
                        productUpsell.redirectToWebsiteWithDiscountCode,
                      discountCode: activeDiscountCode,
                      redirectProvider: resolvedProvider,
                    });
                    upsellRedirect.logUpsellAndRedirect();
                  }
                }}
                style={linkStyles}
                url={shopFullUrl}
              >
                {t("Shop full store")}
              </ExternalLink>
            </Flex>
            {initialMilliseconds && (
              <ExpirationCountdown initialMilliseconds={initialMilliseconds} />
            )}
          </Flex>

          <Flex
            dir="column"
            flex={1}
            inert={environment === TrackingPageEnvironment.BUILDER}
            justify="space-evenly"
          >
            {recommendedProductsLoad.value ? (
              <IterableMap
                items={recommendedProductsLoad.value}
                keyFn={(item: RedoRecommendedProduct) => item.id}
              >
                {(product, index) => (
                  <RecommendedProductComponent
                    buttonText={buttonText}
                    checkoutButtonSettings={checkoutButtonSettings}
                    discountSettings={
                      directDiscountLoad.value?.discountSettings
                    }
                    dropdownSettings={dropdownSettings}
                    hidePrice={hidePrice}
                    imageAspectRatio={imageAspectRatio}
                    index={index}
                    product={product}
                    showCompareAtPrice={showCompareAtPrice}
                    upsellRedirect={
                      environment === TrackingPageEnvironment.CUSTOMER_PORTAL
                        ? createUpsellRedirectHandler({
                            order,
                            selectWindow: productUpsell.selectWindow,
                            returnAppSettings,
                            logPageClick: productUpsell.logPageClick,
                            redirectToWebsiteWithDiscountCode:
                              productUpsell.redirectToWebsiteWithDiscountCode,
                            discountCode: activeDiscountCode,
                            redirectUrl: `${storeURL}/products/${product.handle}`,
                            ctaDestination,
                            redirectProvider: resolvedProvider,
                          })
                        : undefined
                    }
                  />
                )}
              </IterableMap>
            ) : null}
          </Flex>
        </Flex>
      )}
    </BlockWrapper>
  );
});
